import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AutoAwesome as AssistantIcon,
    Close as CloseIcon,
    RestartAlt as NewChatIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useRequest from '../../hooks/useRequest';
import {
    type AiAskResult,
    type AiAssistantStatus,
    type AiChatMessage,
    type AiModelId,
    aiErrorKey,
    answerNoticeKey,
    buildAskHistory,
    canSendQuestion,
    formatUsd,
    MAX_QUESTION_LENGTH,
    resolveSelectedModel,
} from '../../domain/aiAssistant';

const MODEL_STORAGE_KEY = 'askAi.model';
const EXAMPLE_KEYS = ['overview', 'month', 'due', 'howTo'] as const;

/** The last model used on this computer; a convenience that may be unavailable. */
const readRememberedModel = (): string | null => {
    try {
        return localStorage.getItem(MODEL_STORAGE_KEY);
    } catch {
        return null;
    }
};

const rememberModel = (model: AiModelId): void => {
    try {
        localStorage.setItem(MODEL_STORAGE_KEY, model);
    } catch {
        // Storage can be blocked; the default model is then preselected next time.
    }
};

interface AskAiWindowProps {
    status: AiAssistantStatus;
    open: boolean;
    right: number;
    onClose: () => void;
}

interface MessageBubbleProps {
    message: AiChatMessage;
}

const MessageBubble = ({ message }: MessageBubbleProps) => {
    const { t } = useTranslation();

    if (message.kind === 'question') {
        return (
            <Box
                data-testid="ask-ai-question"
                sx={{
                    alignSelf: 'flex-end',
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {message.text}
            </Box>
        );
    }

    if (message.kind === 'error') {
        return (
            <Alert severity="error" data-testid="ask-ai-error" sx={{ alignSelf: 'stretch' }}>
                {t(message.errorKey)}
            </Alert>
        );
    }

    const notice = answerNoticeKey(message.outcome);
    return (
        <Box
            data-testid="ask-ai-answer"
            sx={{
                alignSelf: 'flex-start',
                maxWidth: '92%',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: 'action.hover',
            }}
        >
            {message.text !== '' && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.text}
                </Typography>
            )}
            {notice !== null && (
                <Typography variant="body2" color="warning.main" sx={{ mt: message.text === '' ? 0 : 1 }}>
                    {t(notice)}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t('aiAssistant.window.cost', { cost: formatUsd(message.costUsd) })}
            </Typography>
        </Box>
    );
};

/**
 * The Ask AI chat window: a model picker, the conversation, and the question editor. The
 * conversation lives in this component only, which stays mounted while hidden - closing the
 * window keeps it, a new chat or a page reload clears it.
 */
const AskAiWindow = ({ status, open, right, onClose }: AskAiWindowProps) => {
    const { t, i18n } = useTranslation();
    const request = useRequest();
    const [messages, setMessages] = useState<AiChatMessage[]>([]);
    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(false);
    const [model, setModel] = useState<AiModelId | null>(() =>
        resolveSelectedModel(status, readRememberedModel()),
    );
    const [monthCost, setMonthCost] = useState<number | null>(null);
    const nextId = useRef(0);
    const listEnd = useRef<HTMLDivElement>(null);

    useEffect(() => {
        listEnd.current?.scrollIntoView({ block: 'end' });
    }, [messages, busy]);

    const newId = (): string => {
        nextId.current += 1;
        return String(nextId.current);
    };

    const changeModel = (value: AiModelId) => {
        setModel(value);
        rememberModel(value);
    };

    const send = async () => {
        if (model === null || !canSendQuestion(question, busy)) {
            return;
        }
        const text = question.trim();
        const history = buildAskHistory(messages, text);
        setMessages(previous => [...previous, { id: newId(), kind: 'question', text }]);
        setQuestion('');
        setBusy(true);
        try {
            const response = await request<{ data: AiAskResult }>({
                path: '/ai-assistant/ask',
                method: 'POST',
                params: { model, language: i18n.language.slice(0, 2), messages: history },
            });
            const result = response.data;
            setMessages(previous => [
                ...previous,
                {
                    id: newId(),
                    kind: 'answer',
                    text: result.answer,
                    outcome: result.outcome,
                    model: result.model,
                    costUsd: result.costUsd,
                },
            ]);
            setMonthCost(result.monthToDateCostUsd);
        } catch (error) {
            setMessages(previous => [...previous, { id: newId(), kind: 'error', errorKey: aiErrorKey(error) }]);
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void send();
        }
    };

    return (
        // A native non-modal <dialog>: the browser exposes the dialog role and hides it while
        // closed. Its user-agent box (auto margins, inset, border, padding) is reset here.
        <Paper
            component="dialog"
            open={open}
            elevation={12}
            data-testid="ask-ai-window"
            aria-label={t('aiAssistant.window.title')}
            sx={{
                position: 'fixed',
                inset: 'auto',
                margin: 0,
                padding: 0,
                border: 'none',
                maxWidth: 'none',
                maxHeight: 'none',
                bottom: 88,
                right,
                width: 'min(440px, calc(100vw - 32px))',
                height: 'min(620px, calc(100vh - 120px))',
                zIndex: theme => theme.zIndex.drawer + 2,
                display: open ? 'flex' : 'none',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                }}
            >
                <AssistantIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                        {t('aiAssistant.window.title')}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap display="block">
                        {t('aiAssistant.window.subtitle')}
                    </Typography>
                </Box>
                <Tooltip title={t('aiAssistant.window.newChat')}>
                    <span>
                        <IconButton
                            size="small"
                            color="inherit"
                            aria-label={t('aiAssistant.window.newChat')}
                            data-testid="ask-ai-new-chat"
                            disabled={busy || messages.length === 0}
                            onClick={() => setMessages([])}
                        >
                            <NewChatIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton
                    size="small"
                    color="inherit"
                    aria-label={t('aiAssistant.window.close')}
                    data-testid="ask-ai-close"
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('aiAssistant.window.model')}
                </Typography>
                <Select
                    size="small"
                    value={model ?? ''}
                    onChange={event => changeModel(event.target.value as AiModelId)}
                    disabled={busy}
                    inputProps={{ 'aria-label': t('aiAssistant.window.model'), 'data-testid': 'ask-ai-model' }}
                    sx={{ flex: 1 }}
                >
                    {status.models.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box
                data-testid="ask-ai-messages"
                aria-live="polite"
                sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}
            >
                {messages.length === 0 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {t('aiAssistant.window.empty')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {EXAMPLE_KEYS.map(key => (
                                <Chip
                                    key={key}
                                    size="small"
                                    variant="outlined"
                                    label={t(`aiAssistant.examples.${key}`)}
                                    onClick={() => setQuestion(t(`aiAssistant.examples.${key}`))}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
                {messages.map(message => (
                    <MessageBubble key={message.id} message={message} />
                ))}
                {busy && (
                    <Box data-testid="ask-ai-thinking" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">{t('aiAssistant.window.thinking')}</Typography>
                    </Box>
                )}
                <div ref={listEnd} />
            </Box>

            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        value={question}
                        placeholder={t('aiAssistant.window.placeholder')}
                        onChange={event => setQuestion(event.target.value)}
                        onKeyDown={onKeyDown}
                        slotProps={{
                            htmlInput: {
                                maxLength: MAX_QUESTION_LENGTH,
                                'aria-label': t('aiAssistant.window.placeholder'),
                                'data-testid': 'ask-ai-input',
                            },
                        }}
                    />
                    <Tooltip title={t('aiAssistant.window.send')}>
                        <span>
                            <IconButton
                                color="primary"
                                aria-label={t('aiAssistant.window.send')}
                                data-testid="ask-ai-send"
                                disabled={model === null || !canSendQuestion(question, busy)}
                                onClick={() => void send()}
                            >
                                <SendIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                {monthCost !== null && (
                    <Typography variant="caption" color="text.secondary" data-testid="ask-ai-month-cost">
                        {t('aiAssistant.window.monthCost', { cost: formatUsd(monthCost) })}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default AskAiWindow;
